import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
    }

    /* ── Toast container ─────────────────────────────────────────────────────── */
    .uwc-toast-container {
      position: fixed;
      z-index: ${t.zFloat};
      display: flex;
      flex-direction: column;
      gap: ${t.space2};
      pointer-events: none;
      padding: ${t.space4};
    }

    /* ── Position variants ───────────────────────────────────────────────────── */
    :host([position="top-right"]) .uwc-toast-container {
      top: 0;
      right: 0;
      align-items: flex-end;
    }

    :host([position="top-left"]) .uwc-toast-container {
      top: 0;
      left: 0;
      align-items: flex-start;
    }

    :host([position="top-center"]) .uwc-toast-container {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      align-items: center;
    }

    :host([position="bottom-right"]) .uwc-toast-container {
      bottom: 0;
      right: 0;
      align-items: flex-end;
      flex-direction: column-reverse;
    }

    :host([position="bottom-left"]) .uwc-toast-container {
      bottom: 0;
      left: 0;
      align-items: flex-start;
      flex-direction: column-reverse;
    }

    :host([position="bottom-center"]) .uwc-toast-container {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      align-items: center;
      flex-direction: column-reverse;
    }

    :host([position="center"]) .uwc-toast-container {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      align-items: center;
    }

    /* ── Individual toast item ───────────────────────────────────────────────── */
    .uwc-toast-item {
      pointer-events: all;
      min-width: 280px;
      max-width: 480px;
      border-radius: ${t.radiusMd};
      overflow: hidden;
      box-shadow: ${t.shadowLg};
      background: var(--_bg, var(--uwc-message-info-bg, #e0f2fe));
      border: var(--_border, none);
    }

    /* Default (top-right) animation */
    :host([position="top-right"]) .uwc-toast-item {
      animation: uwc-toast-in-right 220ms ${t.easingStandard} both;
    }

    :host([position="bottom-right"]) .uwc-toast-item {
      animation: uwc-toast-in-right 220ms ${t.easingStandard} both;
    }

    :host([position="top-left"]) .uwc-toast-item {
      animation: uwc-toast-in-left 220ms ${t.easingStandard} both;
    }

    :host([position="bottom-left"]) .uwc-toast-item {
      animation: uwc-toast-in-left 220ms ${t.easingStandard} both;
    }

    :host([position="top-center"]) .uwc-toast-item {
      animation: uwc-toast-in-top 220ms ${t.easingStandard} both;
    }

    :host([position="bottom-center"]) .uwc-toast-item {
      animation: uwc-toast-in-bottom 220ms ${t.easingStandard} both;
    }

    :host([position="center"]) .uwc-toast-item {
      animation: uwc-toast-in-center 220ms ${t.easingStandard} both;
    }

    /* Closing animation overrides all enter animations */
    .uwc-toast-item.is-closing {
      animation: uwc-toast-out 180ms ease-in forwards !important;
    }

    /* ── Severity modifiers ──────────────────────────────────────────────────── */
    .uwc-toast-item--info {
      --_bg:             var(--uwc-message-info-bg,      #e0f2fe);
      --_color:          var(--uwc-message-info-color,   #0369a1);
      --_border-color:   var(--uwc-message-info-border,  #38bdf8);
      --_progress-color: var(--uwc-message-info-border,  #38bdf8);
    }

    .uwc-toast-item--success {
      --_bg:             var(--uwc-message-success-bg,     #dcfce7);
      --_color:          var(--uwc-message-success-color,  #166534);
      --_border-color:   var(--uwc-message-success-border, #22c55e);
      --_progress-color: var(--uwc-message-success-border, #22c55e);
    }

    .uwc-toast-item--warn {
      --_bg:             var(--uwc-message-warn-bg,     #fef9c3);
      --_color:          var(--uwc-message-warn-color,  #b45309);
      --_border-color:   var(--uwc-message-warn-border, #f59e0b);
      --_progress-color: var(--uwc-message-warn-border, #f59e0b);
    }

    .uwc-toast-item--error {
      --_bg:             var(--uwc-message-error-bg,     #fee2e2);
      --_color:          var(--uwc-message-error-color,  #b91c1c);
      --_border-color:   var(--uwc-message-error-border, #ef4444);
      --_progress-color: var(--uwc-message-error-border, #ef4444);
    }

    /* ── Inner layout (mirrors uwc-message) ─────────────────────────────────── */
    .uwc-toast-item__inner {
      display: flex;
      align-items: flex-start;
      gap: ${t.space3};
      padding: ${t.space3} ${t.space4};
    }

    .uwc-toast-item__icon-wrap {
      flex-shrink: 0;
      margin-top: 1px;
      font-size: 1.1rem;
      color: var(--_color);
      display: flex;
      align-items: center;
    }

    .uwc-toast-item__icon {
      color: var(--_color);
    }

    .uwc-toast-item__body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: ${t.space1};
    }

    .uwc-toast-item__summary {
      font-weight: ${t.fontWeightSemibold};
      font-size: ${t.fontSizeMd};
      color: var(--_color);
      line-height: 1.4;
    }

    .uwc-toast-item__detail {
      font-size: ${t.fontSizeMd};
      color: var(--_color);
      opacity: 0.85;
      line-height: 1.5;
    }

    .uwc-toast-item__close {
      flex-shrink: 0;
      margin-left: auto;
      background: none;
      border: none;
      cursor: pointer;
      opacity: 0.6;
      padding: 2px;
      border-radius: ${t.radiusSm};
      color: var(--_color);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity ${t.durationBase}, background ${t.durationBase};
    }

    .uwc-toast-item__close:hover {
      opacity: 1;
      background: color-mix(in oklab, var(--_color) 12%, transparent);
    }

    .uwc-toast-item__close:focus-visible {
      outline: 2px solid var(--_color);
      outline-offset: 2px;
    }

    /* ── Progress bar ────────────────────────────────────────────────────────── */
    .uwc-toast-item__progress {
      height: 3px;
      border-radius: 0 0 ${t.radiusMd} ${t.radiusMd};
      width: 100%;
      animation: uwc-progress-shrink linear forwards;
      background: var(--_progress-color, var(--uwc-message-info-border, #38bdf8));
    }

    /* ── Animations ──────────────────────────────────────────────────────────── */
    @keyframes uwc-toast-in-right {
      from { opacity: 0; transform: translateX(100%); }
      to   { opacity: 1; transform: translateX(0); }
    }

    @keyframes uwc-toast-in-left {
      from { opacity: 0; transform: translateX(-100%); }
      to   { opacity: 1; transform: translateX(0); }
    }

    @keyframes uwc-toast-in-top {
      from { opacity: 0; transform: translateY(-100%); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes uwc-toast-in-bottom {
      from { opacity: 0; transform: translateY(100%); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes uwc-toast-in-center {
      from { opacity: 0; transform: scale(0.9); }
      to   { opacity: 1; transform: scale(1); }
    }

    @keyframes uwc-toast-out {
      from { opacity: 1; transform: scale(1); }
      to   { opacity: 0; transform: scale(0.95); }
    }

    @keyframes uwc-progress-shrink {
      from { width: 100%; }
      to   { width: 0; }
    }

    /* ── Reduced motion ──────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-toast-item {
        animation: none !important;
      }
      .uwc-toast-item.is-closing {
        animation: none !important;
        opacity: 0;
      }
      .uwc-toast-item__progress {
        animation: none;
        width: 0;
      }
    }
  `,
];
